import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  TextField,
  Button,
  DialogActions,
  Box,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import { Formik } from 'formik';
import { environment } from '../../../environments/environment'
import { useSelector, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { loggedOutSuccess, setCart, setUser } from '../../../../../../libs/redux-slices/src';
import { useTranslation } from 'react-i18next';
import { updateUserEmailViaApi } from '../../Utils/userEmailApi';
import { auth } from '@tabletuck/firebase-services';
import { useNavigate } from 'react-router-dom';

const ChangeEmail = ({ open, onClose, setEmail, user, email }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmBeforeChangeOpen, setConfirmBeforeChangeOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.authReducer.userData);
  const [t, i18n] = useTranslation();

  // Clear error when dialog opens/closes
  useEffect(() => {
    if (open) {
      setError('');
      setConfirmBeforeChangeOpen(false);
      setPendingValues(null);
    }
  }, [open]);

  const signOutAndRedirectToLogin = async () => {
    try {
      // Same sign-out logic as Header.js
      await auth.signOut();
      dispatch(loggedOutSuccess());
      dispatch(setCart([]));
      navigate('/signup');
      localStorage.clear();
    } catch (e) {
      console.log(e?.message);
    } finally {
      setConfirmBeforeChangeOpen(false);
      setPendingValues(null);
      onClose();
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setError('');
    try {
      // Basic guards before calling backend
      if (!userData?.uid) {
        throw new Error(
          'User information not available. Please try logging in again.'
        );
      }

      // Reusable backend call
      await updateUserEmailViaApi({
        email: values.email,
        userId: userData.uid,
      });

      // Update Redux state optimistically after successful API call
      const updatedUser = { ...userData, email: values.email };
      dispatch(setUser({ userData: updatedUser }));

      // Update parent UI only after backend update succeeded
      if (typeof setEmail === 'function') setEmail(values.email);

      setLoading(false);

      // Email changed successfully → immediately require fresh login
      // (You can add a global toast here if you have one)
      await signOutAndRedirectToLogin();
    } catch (e) {
      setLoading(false);
      console.error('Error updating email:', e);

      // Token expired / re-auth required -> sign out and redirect to login
      if (
        e?.code === 'auth/user-token-expired' ||
        e?.code === 'auth/requires-recent-login' ||
        e?.message?.includes("credential is no longer valid") ||
        e?.message?.includes('sign in again')
      ) {
        setError(
          "Your session has expired. You'll need to sign in again to change your email."
        );
        await signOutAndRedirectToLogin();
        return;
      }

      // Provide user-friendly error messages
      if (e?.message) {
        setError(e.message);
      } else {
        setError('Failed to update email. Please try again.');
      }
    }
  };

  const validationScheme = yup.object().shape({
    email: yup
      .string()
      .email(t('profilee.emailNotVaild'))
      .required(t('profilee.enterNewEmail')),
  });

  return (
    <>
      <Dialog
        maxWidth="sm"
        fullWidth
        open={open}
        onClose={() => onClose()}
        aria-labelledby="change-email-dialog"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        {/* Header with icon and close button */}
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${environment.mainColor}20, ${environment.mainColor}10)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <EmailIcon
                  sx={{
                    color: environment.mainColor,
                    fontSize: 24,
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: '20px',
                    color: '#172B4D',
                    fontFamily: 'Jost, sans-serif',
                  }}
                >
                  {t('profilee.changeEmail')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6B7280',
                    fontSize: '14px',
                    fontFamily: 'Jost, sans-serif',
                  }}
                >
                  Update your email address
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => onClose()}
              sx={{
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Formik
            enableReinitialize
            initialValues={{
              email: email ? email : user?.email,
            }}
            onSubmit={(values) => {
              // Confirm BEFORE starting email change (security-sensitive operation)
              setPendingValues(values);
              setConfirmBeforeChangeOpen(true);
            }}
            validationSchema={validationScheme}
          >
            {({
              handleBlur,
              handleChange,
              values,
              errors,
              handleSubmit,
              touched,
            }) => (
              <Box sx={{ p: 3 }}>
                <TextField
                  fullWidth
                  label={t('profilee.email')}
                  name="email"
                  type="email"
                  placeholder="Enter your email address"
                  onChange={handleChange('email')}
                  onBlur={handleBlur('email')}
                  required
                  value={values.email}
                  autoComplete="email"
                  error={!!(errors.email && touched.email)}
                  helperText={errors.email && touched.email ? errors.email : ''}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: '#F9FAFB',
                      fontFamily: 'Jost, sans-serif',
                      '& fieldset': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover fieldset': {
                        borderColor: environment.mainColor,
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: environment.mainColor,
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 500,
                      '&.Mui-focused': {
                        color: environment.mainColor,
                      },
                    },
                  }}
                  variant="outlined"
                />

                {/* API/Update error/success messages */}
                {error && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      backgroundColor: error.includes('successfully')
                        ? '#F0FDF4'
                        : '#FEF2F2',
                      borderRadius: '8px',
                      border: error.includes('successfully')
                        ? '1px solid #86EFAC'
                        : '1px solid #FCA5A5',
                    }}
                  >
                    <Typography
                      sx={{
                        color: error.includes('successfully')
                          ? '#16A34A'
                          : '#DC2626',
                        fontSize: '13px',
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      {error}
                    </Typography>
                  </Box>
                )}

                {/* Action buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    mt: 4,
                  }}
                >
                  <Button
                    onClick={() => onClose()}
                    variant="outlined"
                    sx={{
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 600,
                      fontSize: '15px',
                      padding: '10px 24px',
                      borderColor: '#E5E7EB',
                      color: '#374151',
                      '&:hover': {
                        borderColor: '#D1D5DB',
                        backgroundColor: '#F9FAFB',
                      },
                    }}
                  >
                    {t('profilee.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !values.email || errors.email}
                    startIcon={
                      loading ? (
                        <CircularProgress size={16} sx={{ color: 'white' }} />
                      ) : null
                    }
                    sx={{
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 600,
                      fontSize: '15px',
                      padding: '10px 24px',
                      backgroundColor:
                        loading || !values.email || errors.email
                          ? '#D1D5DB'
                          : environment.mainColor,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor:
                          loading || !values.email || errors.email
                            ? '#D1D5DB'
                            : environment.mainColor,
                        opacity: 0.9,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      },
                      '&:disabled': {
                        backgroundColor: '#D1D5DB',
                        color: 'white',
                      },
                    }}
                  >
                    {loading ? 'Saving...' : t('profilee.saveInfo')}
                  </Button>
                </Box>
              </Box>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Confirm BEFORE changing email (may require re-login) */}
      <Dialog
        open={confirmBeforeChangeOpen}
        onClose={() => setConfirmBeforeChangeOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="confirm-change-email-dialog"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 12px 30px rgba(15, 23, 42, 0.25)',
          },
        }}
      >
        <DialogTitle
          id="confirm-change-email-dialog"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pb: 0,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${environment.mainColor}20, ${environment.mainColor}10)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EmailIcon
              sx={{
                color: environment.mainColor,
                fontSize: 18,
              }}
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                fontSize: '18px',
                color: '#111827',
              }}
            >
              Confirm email change
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '12px',
                color: '#6B7280',
              }}
            >
              You are about to update the email associated with your account.
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: '12px',
              backgroundColor: '#FEF3C7',
              border: '1px solid #FACC15',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '13px',
                color: '#92400E',
              }}
            >
              Changing your email is a security‑sensitive action. For your
              protection, you may be asked to sign in again. If that happens,
              we&apos;ll log you out and redirect you to the login page so you
              can continue safely.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setConfirmBeforeChangeOpen(false);
              setPendingValues(null);
            }}
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              borderColor: '#E5E7EB',
              color: '#374151',
              '&:hover': {
                borderColor: '#D1D5DB',
                backgroundColor: '#F9FAFB',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setConfirmBeforeChangeOpen(false);
              if (pendingValues) {
                await handleSubmit(pendingValues);
              }
              setPendingValues(null);
            }}
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '14px',
              px: 3,
              backgroundColor: environment.mainColor,
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                backgroundColor: environment.mainColor,
                opacity: 0.95,
              },
            }}
          >
            Confirm &amp; continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChangeEmail;
